import { ApiClient, type ApiRequestOptions } from "@/api/client";
import { getRuntimeConfig } from "@/lib/runtime-config";
import { useAuthStore } from "@/stores/auth";

class AuthAwareClient extends ApiClient {
  private store: ReturnType<typeof useAuthStore>;

  constructor(baseUrl: string, store: ReturnType<typeof useAuthStore>) {
    super(baseUrl);
    this.store = store;
  }

  private async authedHeaders(opts: ApiRequestOptions): Promise<Record<string, string>> {
    const tok = await this.store.ensureFreshToken();
    const headers = { ...(opts.headers ?? {}) };
    if (tok) headers.Authorization = `Bearer ${tok}`;
    return headers;
  }

  private handleStatus(err: unknown): void {
    const status = (err as { status?: number }).status;
    if (status === 401) {
      this.store.signOut();
    }
  }

  override async get<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.get<T>(path, { ...opts, headers });
    } catch (err: unknown) {
      // A read the API answers 503 (the ledger waking up after idle) is tried once more.
      if ((err as { status?: number }).status === 503 && !opts.signal?.aborted) {
        await new Promise((r) => setTimeout(r, 3000));
        try {
          return await super.get<T>(path, { ...opts, headers: await this.authedHeaders(opts) });
        } catch (again: unknown) {
          this.handleStatus(again);
          throw again;
        }
      }
      this.handleStatus(err);
      throw err;
    }
  }

  override async blob(path: string, opts: ApiRequestOptions = {}): Promise<Blob> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.blob(path, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }

  override async patch<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.patch<T>(path, body, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }

  override async post<T>(path: string, body?: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.post<T>(path, body, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }

  override async put<T>(path: string, body: unknown, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.put<T>(path, body, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }

  override async del<T>(path: string, opts: ApiRequestOptions = {}): Promise<T> {
    const headers = await this.authedHeaders(opts);
    try {
      return await super.del<T>(path, { ...opts, headers });
    } catch (err: unknown) {
      this.handleStatus(err);
      throw err;
    }
  }
}

// Set only by main.ts in dev mock mode (src/api/mock), which it loads with a
// dynamic import so production bundles never contain the fixtures.
let override: ApiClient | null = null;
export function setClientOverride(c: ApiClient): void {
  override = c;
}

export function useApi() {
  if (override) return { client: override };
  const cfg = getRuntimeConfig();
  const store = useAuthStore();
  const client = new AuthAwareClient(cfg.apiEndpoint, store);
  return { client };
}
